import React, {
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useState,
  useLayoutEffect,
  useEffect,
} from "react"
import { View } from "./primitives"

export const GestureHandlerRootView = View

type GestureEvent = {
  translationX: number
  translationY: number
}

type GestureHandlers = {
  minDuration?: number
  activeOffsetY?: number
  failOffsetY?: number
  onStart?: (e?: GestureEvent) => void
  onUpdate?: (e: GestureEvent) => void
  onEnd?: (e?: GestureEvent) => void
  onFinalize?: (e?: GestureEvent) => void
}

type BuiltGesture = GestureHandlers & {
  _handlers: () => GestureHandlers
}

function createGesture(): BuiltGesture & {
  minDuration: (ms: number) => BuiltGesture
  activeOffsetY: (y: number) => BuiltGesture
  failOffsetY: (y: number) => BuiltGesture
  onStart: (fn: (e?: GestureEvent) => void) => BuiltGesture
  onUpdate: (fn: (e: GestureEvent) => void) => BuiltGesture
  onEnd: (fn: (e?: GestureEvent) => void) => BuiltGesture
  onFinalize: (fn: (e?: GestureEvent) => void) => BuiltGesture
} {
  const handlers: GestureHandlers = {}
  const gesture = {
    minDuration(ms: number) {
      handlers.minDuration = ms
      return gesture
    },
    activeOffsetY(y: number) {
      handlers.activeOffsetY = y
      return gesture
    },
    failOffsetY(y: number) {
      handlers.failOffsetY = y
      return gesture
    },
    onStart(fn: (e?: GestureEvent) => void) {
      handlers.onStart = fn
      return gesture
    },
    onUpdate(fn: (e: GestureEvent) => void) {
      handlers.onUpdate = fn
      return gesture
    },
    onEnd(fn: (e?: GestureEvent) => void) {
      handlers.onEnd = fn
      return gesture
    },
    onFinalize(fn: (e?: GestureEvent) => void) {
      handlers.onFinalize = fn
      return gesture
    },
    _handlers: () => handlers,
  }
  return gesture
}

export const Gesture = {
  Pan: () => createGesture(),
  Tap: () => createGesture(),
  LongPress: () => createGesture(),
  Simultaneous: (...gestures: BuiltGesture[]) => ({ _type: "simultaneous" as const, gestures }),
}

function collectHandlers(gesture: unknown): BuiltGesture[] {
  if (!gesture || typeof gesture !== "object") return []
  const record = gesture as { _type?: string; gestures?: BuiltGesture[] }
  if (record._type === "simultaneous" && record.gestures) {
    return record.gestures
  }
  return [gesture as BuiltGesture]
}

type GestureMode = "pending" | "pan" | "longPress" | "cancelled"

export function GestureDetector({
  gesture,
  children,
}: {
  gesture: unknown
  children: React.ReactNode
}) {
  const gestures = collectHandlers(gesture)
  const longPressGesture = gestures.find((g) => (g._handlers().minDuration ?? 0) > 0)
  const panGesture = gestures.find((g) => g._handlers().onUpdate)

  const longPressHandlers = longPressGesture?._handlers() ?? {}
  const panHandlers = panGesture?._handlers() ?? {}

  const hasLongPress = (longPressHandlers.minDuration ?? 0) > 0
  const hasPan = Boolean(panHandlers.onUpdate)
  const activeOffsetY = Math.abs(panHandlers.activeOffsetY ?? 10)
  const failOffsetY = Math.abs(panHandlers.failOffsetY ?? 10)

  const rootRef = useRef<HTMLDivElement>(null)
  const trackingRef = useRef(false)
  const didPanRef = useRef(false)
  const onWindowPointerMoveRef = useRef<(e: PointerEvent) => void>(() => {})
  const onWindowPointerUpRef = useRef<(e: PointerEvent) => void>(() => {})

  const stateRef = useRef({
    startX: 0,
    startY: 0,
    longPressTimer: null as ReturnType<typeof setTimeout> | null,
    mode: "pending" as GestureMode,
    pointerId: -1,
  })

  const clearLongPressTimer = useCallback(() => {
    if (stateRef.current.longPressTimer) {
      clearTimeout(stateRef.current.longPressTimer)
      stateRef.current.longPressTimer = null
    }
  }, [])

  const releaseCapture = useCallback((pointerId: number) => {
    const root = rootRef.current
    if (!root) return
    try {
      root.releasePointerCapture(pointerId)
    } catch {
      // pointer may already be released
    }
  }, [])

  const onWindowPointerMoveStable = useCallback((e: PointerEvent) => {
    onWindowPointerMoveRef.current(e)
  }, [])

  const onWindowPointerUpStable = useCallback((e: PointerEvent) => {
    onWindowPointerUpRef.current(e)
  }, [])

  const stopTracking = useCallback(() => {
    if (!trackingRef.current) return
    trackingRef.current = false
    window.removeEventListener("pointermove", onWindowPointerMoveStable)
    window.removeEventListener("pointerup", onWindowPointerUpStable)
    window.removeEventListener("pointercancel", onWindowPointerUpStable)
  }, [onWindowPointerMoveStable, onWindowPointerUpStable])

  const resetPointer = useCallback(
    (pointerId: number) => {
      clearLongPressTimer()
      releaseCapture(pointerId)
      stateRef.current.pointerId = -1
      stateRef.current.mode = "pending"
    },
    [clearLongPressTimer, releaseCapture],
  )

  const onWindowPointerMove = useCallback(
    (e: PointerEvent) => {
      if (e.pointerId !== stateRef.current.pointerId) return

      const translationX = e.clientX - stateRef.current.startX
      const translationY = e.clientY - stateRef.current.startY

      if (stateRef.current.mode === "pending") {
        if (Math.abs(translationX) > 8 || Math.abs(translationY) > 8) {
          clearLongPressTimer()
        }

        if (hasPan) {
          if (translationY > activeOffsetY && Math.abs(translationY) > Math.abs(translationX)) {
            stateRef.current.mode = "pan"
            didPanRef.current = true
            try {
              rootRef.current?.setPointerCapture(e.pointerId)
            } catch {
              // ignore
            }
            panHandlers.onStart?.({ translationX, translationY })
            panHandlers.onUpdate?.({ translationX, translationY })
            return
          }
          if (translationY < -failOffsetY) {
            stateRef.current.mode = "cancelled"
            stopTracking()
            resetPointer(e.pointerId)
            return
          }
        }
        return
      }

      if (stateRef.current.mode === "longPress") {
        panHandlers.onUpdate?.({ translationX, translationY })
        return
      }

      if (stateRef.current.mode === "pan") {
        didPanRef.current = true
        e.preventDefault()
        panHandlers.onUpdate?.({ translationX, translationY })
      }
    },
    [
      activeOffsetY,
      clearLongPressTimer,
      failOffsetY,
      hasPan,
      panHandlers,
      resetPointer,
      stopTracking,
    ],
  )

  const onWindowPointerUp = useCallback(
    (e: PointerEvent) => {
      if (e.pointerId !== stateRef.current.pointerId) return

      const translationX = e.clientX - stateRef.current.startX
      const translationY = e.clientY - stateRef.current.startY
      const event = { translationX, translationY }
      const mode = stateRef.current.mode

      stopTracking()

      if (mode === "longPress") {
        longPressHandlers.onEnd?.(event)
        panHandlers.onFinalize?.(event)
      } else if (mode === "pan") {
        panHandlers.onEnd?.(event)
        panHandlers.onFinalize?.(event)
      }

      resetPointer(e.pointerId)
    },
    [longPressHandlers, panHandlers, resetPointer, stopTracking],
  )

  onWindowPointerMoveRef.current = onWindowPointerMove
  onWindowPointerUpRef.current = onWindowPointerUp

  useEffect(() => () => stopTracking(), [stopTracking])

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!hasPan && !hasLongPress) return
      if (e.button !== 0) return
      if (stateRef.current.pointerId !== -1) return

      stateRef.current.pointerId = e.pointerId
      stateRef.current.startX = e.clientX
      stateRef.current.startY = e.clientY
      stateRef.current.mode = "pending"
      didPanRef.current = false

      if (hasLongPress) {
        clearLongPressTimer()
        const duration = longPressHandlers.minDuration ?? 500
        const pointerId = e.pointerId
        stateRef.current.longPressTimer = setTimeout(() => {
          if (stateRef.current.pointerId !== pointerId) return
          stateRef.current.mode = "longPress"
          try {
            rootRef.current?.setPointerCapture(pointerId)
          } catch {
            // ignore
          }
          longPressHandlers.onStart?.()
          panHandlers.onStart?.({ translationX: 0, translationY: 0 })
        }, duration)
      }

      trackingRef.current = true
      window.addEventListener("pointermove", onWindowPointerMoveStable)
      window.addEventListener("pointerup", onWindowPointerUpStable)
      window.addEventListener("pointercancel", onWindowPointerUpStable)
    },
    [
      clearLongPressTimer,
      hasLongPress,
      hasPan,
      longPressHandlers,
      onWindowPointerMoveStable,
      onWindowPointerUpStable,
      panHandlers,
    ],
  )

  if (!hasPan && !hasLongPress) {
    return <>{children}</>
  }

  return (
    <div
      ref={rootRef}
      style={{
        width: "auto",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        touchAction: hasPan ? "pan-y" : "none",
      }}
      onPointerDown={onPointerDown}
      onClickCapture={(e) => {
        if (didPanRef.current) {
          e.preventDefault()
          e.stopPropagation()
          didPanRef.current = false
        }
      }}
    >
      {children}
    </div>
  )
}

export const ScrollView = View
export const FlatList = View

const SWIPE_TRANSITION = "transform 0.2s ease-out"
/** 竖向滚动判定：iOS 轻扫时横向漂移较大，需更偏向纵向 */
const VERTICAL_SCROLL_MIN = 10
const HORIZONTAL_DOMINANCE_DELTA = 10
const HORIZONTAL_ACTIVATION_MIN = 14
/** 未真正滑开前不露出操作按钮，避免一闪 */
const ACTIONS_REVEAL_OFFSET = 12
const SWIPE_VISUAL_COMMIT = 16

function isVerticalScrollIntent(absDx: number, absDy: number) {
  return absDy >= VERTICAL_SCROLL_MIN && absDy >= absDx
}

function isHorizontalSwipeIntent(absDx: number, absDy: number) {
  return absDx >= HORIZONTAL_ACTIVATION_MIN && absDx > absDy + HORIZONTAL_DOMINANCE_DELTA
}

function parseAxisOffset(value: number | [number, number] | undefined, index: 0 | 1, fallback: number) {
  if (value == null) return fallback
  if (Array.isArray(value)) return Math.abs(value[index] ?? fallback)
  return Math.abs(value)
}

export type SwipeableProps = {
  children?: React.ReactNode
  renderRightActions?: () => React.ReactNode
  renderLeftActions?: () => React.ReactNode
  activeOffsetX?: number | [number, number]
  failOffsetY?: number | [number, number]
  enabled?: boolean
}

type SwipeMode = "pending" | "swipe" | "scroll"

export const ReanimatedSwipeable = forwardRef<{ close: () => void }, SwipeableProps>(
  function ReanimatedSwipeable(
    {
      children,
      renderRightActions,
      renderLeftActions,
      activeOffsetX = [-35, 35],
      failOffsetY = [-22, 22],
      enabled = true,
    },
    ref,
  ) {
    const [offsetX, setOffsetX] = useState(0)
    const [dragging, setDragging] = useState(false)

    const actionsWidthRef = useRef(0)
    const rightActionsRef = useRef<HTMLDivElement>(null)
    const leftActionsRef = useRef<HTMLDivElement>(null)
    const contentRef = useRef<HTMLDivElement>(null)
    const offsetXRef = useRef(0)
    const didSwipeRef = useRef(false)
    const trackingRef = useRef(false)
    const onWindowPointerMoveRef = useRef<(e: PointerEvent) => void>(() => {})
    const onWindowPointerUpRef = useRef<(e: PointerEvent) => void>(() => {})

    const dragRef = useRef({
      pointerId: -1,
      startX: 0,
      startY: 0,
      startOffsetX: 0,
      mode: "pending" as SwipeMode,
    })

    const thresholdsRef = useRef({
      activeLeft: 35,
      activeRight: 35,
      failY: 22,
    })

    thresholdsRef.current = {
      activeLeft: parseAxisOffset(activeOffsetX, 0, 35),
      activeRight: parseAxisOffset(activeOffsetX, 1, 35),
      failY: parseAxisOffset(failOffsetY, 0, 22),
    }

    const measureActions = useCallback(() => {
      const right = rightActionsRef.current?.offsetWidth ?? 0
      const left = leftActionsRef.current?.offsetWidth ?? 0
      actionsWidthRef.current = Math.max(right, left, right || left)
      return { right, left }
    }, [])

    useLayoutEffect(() => {
      measureActions()
    }, [renderRightActions, renderLeftActions, measureActions])

    const clampOffset = useCallback(
      (value: number) => {
        const right = rightActionsRef.current?.offsetWidth ?? 0
        const left = leftActionsRef.current?.offsetWidth ?? 0
        const min = renderRightActions ? -right : 0
        const max = renderLeftActions ? left : 0
        return Math.min(max, Math.max(min, value))
      },
      [renderLeftActions, renderRightActions],
    )

    const snapTo = useCallback(
      (target: number) => {
        const next = clampOffset(target)
        offsetXRef.current = next
        setDragging(false)
        setOffsetX(next)
      },
      [clampOffset],
    )

    const close = useCallback(() => snapTo(0), [snapTo])

    useImperativeHandle(ref, () => ({ close }), [close])

    const releaseCapture = useCallback((pointerId: number) => {
      const content = contentRef.current
      if (!content) return
      try {
        content.releasePointerCapture(pointerId)
      } catch {
        // pointer may already be released
      }
    }, [])

    const resetDragState = useCallback(() => {
      dragRef.current.pointerId = -1
      dragRef.current.mode = "pending"
    }, [])

    const onWindowPointerMoveStable = useCallback((e: PointerEvent) => {
      onWindowPointerMoveRef.current(e)
    }, [])

    const onWindowPointerUpStable = useCallback((e: PointerEvent) => {
      onWindowPointerUpRef.current(e)
    }, [])

    const stopTracking = useCallback(() => {
      if (!trackingRef.current) return
      trackingRef.current = false
      window.removeEventListener("pointermove", onWindowPointerMoveStable)
      window.removeEventListener("pointerup", onWindowPointerUpStable)
      window.removeEventListener("pointercancel", onWindowPointerUpStable)
    }, [onWindowPointerMoveStable, onWindowPointerUpStable])

    const abandonToScroll = useCallback(() => {
      const startOffset = dragRef.current.startOffsetX
      dragRef.current.mode = "scroll"
      stopTracking()
      if (offsetXRef.current !== startOffset) {
        snapTo(startOffset)
      }
      resetDragState()
      setDragging(false)
    }, [resetDragState, snapTo, stopTracking])

    const onWindowPointerMove = useCallback(
      (e: PointerEvent) => {
        if (e.pointerId !== dragRef.current.pointerId) return

        const dx = e.clientX - dragRef.current.startX
        const dy = e.clientY - dragRef.current.startY
        const { activeLeft, activeRight, failY } = thresholdsRef.current
        const startOffset = dragRef.current.startOffsetX
        const content = contentRef.current

        if (dragRef.current.mode === "pending") {
          const absDx = Math.abs(dx)
          const absDy = Math.abs(dy)

          if (isVerticalScrollIntent(absDx, absDy) || (absDy > failY && absDy > absDx)) {
            abandonToScroll()
            return
          }

          if (!isHorizontalSwipeIntent(absDx, absDy)) {
            return
          }

          const isOpen = startOffset !== 0

          if (isOpen && absDx > 2) {
            dragRef.current.mode = "swipe"
          } else if (!isOpen && dx <= -activeLeft && renderRightActions) {
            dragRef.current.mode = "swipe"
          } else if (!isOpen && dx >= activeRight && renderLeftActions) {
            dragRef.current.mode = "swipe"
          } else if (isOpen && dx > 0 && renderRightActions && startOffset < 0) {
            dragRef.current.mode = "swipe"
          } else if (isOpen && dx < 0 && renderLeftActions && startOffset > 0) {
            dragRef.current.mode = "swipe"
          } else {
            return
          }
        }

        if (dragRef.current.mode !== "swipe") return

        if (startOffset === 0 && Math.abs(dx) < SWIPE_VISUAL_COMMIT) {
          return
        }

        if (content) {
          try {
            content.setPointerCapture(e.pointerId)
          } catch {
            // ignore
          }
        }
        setDragging(true)

        if (Math.abs(dx) > 5) {
          didSwipeRef.current = true
        }

        e.preventDefault()
        const next = clampOffset(startOffset + dx)
        offsetXRef.current = next
        setOffsetX(next)
      },
      [abandonToScroll, clampOffset, renderLeftActions, renderRightActions],
    )

    const onWindowPointerUp = useCallback(
      (e: PointerEvent) => {
        if (e.pointerId !== dragRef.current.pointerId) return

        const mode = dragRef.current.mode
        const dx = e.clientX - dragRef.current.startX
        stopTracking()
        releaseCapture(e.pointerId)
        resetDragState()
        setDragging(false)

        if (mode !== "swipe") return

        const right = rightActionsRef.current?.offsetWidth ?? 0
        const left = leftActionsRef.current?.offsetWidth ?? 0
        const current = offsetXRef.current

        if (renderRightActions && right > 0) {
          const openAt = -right
          if (dx > 8) {
            snapTo(0)
          } else if (dx < -8 && current <= openAt / 2) {
            snapTo(openAt)
          } else {
            snapTo(current <= openAt / 2 ? openAt : 0)
          }
          return
        }

        if (renderLeftActions && left > 0) {
          const openAt = left
          if (dx < -8) {
            snapTo(0)
          } else if (dx > 8 && current >= openAt / 2) {
            snapTo(openAt)
          } else {
            snapTo(current >= openAt / 2 ? openAt : 0)
          }
          return
        }

        snapTo(0)
      },
      [releaseCapture, renderLeftActions, renderRightActions, resetDragState, snapTo, stopTracking],
    )

    onWindowPointerMoveRef.current = onWindowPointerMove
    onWindowPointerUpRef.current = onWindowPointerUp

    useEffect(() => {
      return () => {
        stopTracking()
      }
    }, [stopTracking])

    const onPointerDown = useCallback(
      (e: React.PointerEvent<HTMLDivElement>) => {
        if (!enabled || (!renderRightActions && !renderLeftActions)) return
        if (e.button !== 0) return

        measureActions()
        didSwipeRef.current = false
        dragRef.current = {
          pointerId: e.pointerId,
          startX: e.clientX,
          startY: e.clientY,
          startOffsetX: offsetXRef.current,
          mode: "pending",
        }
        trackingRef.current = true
        window.addEventListener("pointermove", onWindowPointerMoveStable)
        window.addEventListener("pointerup", onWindowPointerUpStable)
        window.addEventListener("pointercancel", onWindowPointerUpStable)
      },
      [
        enabled,
        measureActions,
        onWindowPointerMoveStable,
        onWindowPointerUpStable,
        renderLeftActions,
        renderRightActions,
      ],
    )

    const hasActions = Boolean(renderRightActions || renderLeftActions)
    const actionsVisible = Math.abs(offsetX) >= ACTIONS_REVEAL_OFFSET

    return (
      <div
        className="rn-swipeable"
        style={{
          position: "relative",
          overflow: "hidden",
          width: "100%",
          touchAction: dragging ? "none" : "pan-y",
        }}
      >
        {renderRightActions ? (
          <div
            ref={rightActionsRef}
            className="rn-swipeable__actions rn-swipeable__actions--right"
            style={{
              position: "absolute",
              inset: "0 0 0 auto",
              display: "flex",
              flexDirection: "row",
              alignItems: "stretch",
              height: "100%",
              opacity: actionsVisible ? 1 : 0,
              pointerEvents: actionsVisible ? "auto" : "none",
            }}
          >
            {renderRightActions()}
          </div>
        ) : null}
        {renderLeftActions ? (
          <div
            ref={leftActionsRef}
            className="rn-swipeable__actions rn-swipeable__actions--left"
            style={{
              position: "absolute",
              inset: "0 auto 0 0",
              display: "flex",
              flexDirection: "row",
              alignItems: "stretch",
              height: "100%",
              opacity: actionsVisible ? 1 : 0,
              pointerEvents: actionsVisible ? "auto" : "none",
            }}
          >
            {renderLeftActions()}
          </div>
        ) : null}
        <div
          ref={contentRef}
          className="rn-swipeable__content"
          style={{
            position: "relative",
            transform: `translate3d(${offsetX}px, 0, 0)`,
            transition: dragging ? "none" : SWIPE_TRANSITION,
            willChange: dragging ? "transform" : undefined,
            touchAction: hasActions ? (dragging ? "none" : "pan-y") : undefined,
          }}
          onPointerDown={onPointerDown}
          onClickCapture={(e) => {
            if (didSwipeRef.current) {
              e.preventDefault()
              e.stopPropagation()
              didSwipeRef.current = false
            }
          }}
        >
          {children}
        </div>
      </div>
    )
  },
)

export default { GestureHandlerRootView, GestureDetector, Gesture, ReanimatedSwipeable, Swipeable: ReanimatedSwipeable }
